'use client'
import Image from 'next/image'
import { useMemo } from 'react'
import { PrismicRichText } from '@prismicio/react'
import { smoothScrollToElement } from '@/utils/smoothScroll'
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next'

interface Framework {
  framework: string
}

type DataObject = Record<string, any>
type ItemObject = Record<string, any>
type ExploreFrameworksProps = {
  data: DataObject
  frameworks?: Array<Framework>
}

const ExploreFrameworksComponent = ({ data, frameworks }: ExploreFrameworksProps) => {
  const renderToExploreFrameworks = (key: string, eFrameworks: DataObject) => (
    <div key={key}>
      <div className="my-8 rounded-[5px] bg-secondary-gray-100 py-9 text-center">
        <h3 id={key.replace(/\s/g, '')}>{key}</h3>
      </div>

      <div className="relative grid grid-cols-1 gap-8 md:grid-cols-2 xl:grid-cols-3">
        {eFrameworks.map((item: ItemObject, index: number) => {
          const key = `i-${index}`
          return (
            <div className="rounded-md border border-primary-ink-6 shadow-light backdrop-blur-25" key={key}>
              <div className="flex items-start gap-4 rounded-t-md bg-primary-green-20 px-4 py-6 md:px-11 md:py-8">
                <PrismicNextImage field={item.icon} className="h-[30px] w-[30px]" fallbackAlt="" />
                <PrismicRichText
                  field={item.title}
                  components={{
                    heading4: ({ children }) => (
                      <h4 className="relative top-0.5 whitespace-nowrap text-[28px] font-medium !leading-[110%] -tracking-[0.45px]">
                        {children}
                      </h4>
                    ),
                  }}
                />
              </div>

              <div className="px-4 pt-6 md:px-11 [&>p]:min-h-[320px] [&>p]:text-lg [&>p]:font-normal [&>p]:leading-[140%] [&>p]:tracking-[0.22px] [&>p]:text-primary-ink-85 [&>ul>li]:relative [&>ul>li]:mb-6 [&>ul>li]:pl-8 [&>ul>li]:text-base [&>ul>li]:font-normal [&>ul>li]:leading-[140%] [&>ul>li]:tracking-[0.16px] [&>ul>li]:before:absolute [&>ul>li]:before:left-0 [&>ul>li]:before:top-0 [&>ul>li]:before:h-[20px] [&>ul>li]:before:w-[20px] [&>ul>li]:before:bg-[url('/images/check.svg')] [&>ul>li]:before:content-[''] [&>ul]:mb-16 [&>ul]:mt-4">
                <PrismicRichText field={item.description} />
              </div>

              <div className="px-4 pb-6 md:px-11 md:pb-11">
                {item.coming_soon ? (
                  <span className="font-halyardDisplay text-base font-medium leading-[140%] tracking-[0.16px]">
                    Coming soon
                  </span>
                ) : (
                  <PrismicNextLink
                    field={item.btn_link}
                    className="btn btn-default btn-text btn-interaction !w-fit !p-0">
                    <span className="font-halyardDisplay text-base font-medium leading-[140%] tracking-[0.16px]">
                      {item.btn_label}
                    </span>
                    {item.btn_label && (
                      <Image src="/images/angle-right-green.svg" width={15} height={9} alt="" aria-hidden="true" />
                    )}
                  </PrismicNextLink>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )

  const exploreFrameworks = useMemo(() => {
    const renderToExploreFrameworksData: Record<string, ItemObject[]> = {}

    data.items.map((item: any) => {
      if (!(item.framework_type in renderToExploreFrameworksData)) {
        renderToExploreFrameworksData[item.framework_type] = []
      }
      renderToExploreFrameworksData[item.framework_type].push(item)
    })

    return Object.keys(renderToExploreFrameworksData).map((key) => {
      return renderToExploreFrameworks(key, renderToExploreFrameworksData[key].flat())
    })
  }, [data])

  return (
    <section className="pb-14 pt-6">
      <div className="container">
        <div className="mb-24 mt-14 text-center">
          <div className="flex flex-wrap justify-center gap-4">
            {frameworks?.map((item: Framework, index: number) => {
              const targetId = item.framework.replace(/\s/g, '')
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => {
                    smoothScrollToElement(targetId, 130)
                    // Update URL hash without causing page jump
                    window.history.pushState(null, '', `#${targetId}`)
                  }}
                  className="cursor-pointer">
                  <div className="rounded-[5px] bg-primary-ink-6 p-2">{item.framework}</div>
                </button>
              )
            })}
          </div>
        </div>
        {exploreFrameworks}
      </div>
    </section>
  )
}

export default ExploreFrameworksComponent
